import type { Field as RawField } from "../../../types/rawProcess.types";
import { dbOps } from "../hooks/dbOps.store";
import { fieldModelCreator } from "../../../models/field.model";
import {
  insertItemAtIndex,
  moveItemToAnotherArray,
  reorderItems
} from "@certa/common/utils/array";
import { createField, type Field } from "@certa/studio-core";
import { addStep } from "./step.actions";
import { importRulesToStudio } from "./rule.actions";
import { Intents } from "@certa/studio-core/src/constants/activityLogs.constants";

export const importRawFields = (props: {
  rawFields: RawField[];
  processId: string;
  stepId: string;
  intent:
    | Intents.IMPORT_FIELDS_CSV_IN_STEP
    | Intents.IMPORT_STEP_TEMPLATE_IN_EXISTING_STEP;
  templateId?: string;
}) => {
  const { rawFields, processId, stepId, intent, templateId } = props;
  dbOps().transaction(
    () => {
      const step = dbOps().getStep(processId, stepId);
      for (const fieldJSON of rawFields) {
        const { field, conditionGroupMap, conditionMap, ruleMap } =
          fieldModelCreator(fieldJSON, { processId });
        step.fields.push(field.id);
        dbOps().updateStep(processId, stepId, { fields: step.fields });
        dbOps().setField(processId, field);
        importRulesToStudio({
          processId,
          ruleMap,
          conditionMap,
          conditionGroupMap
        });
      }
    },
    { intent, templateId }
  );
};
export const addFieldsIntoAStep = ({
  processId,
  stepId,
  fields
}: {
  processId: string;
  stepId: string;
  fields: Field[];
}) => {
  return dbOps().transaction(
    () => {
      const step = dbOps().getStep(processId, stepId);
      if (!step) return;

      fields.forEach(field => {
        dbOps().addField(processId, field);
        step.fields.push(field.id);
      });

      dbOps().updateStep(processId, stepId, { fields: step.fields });
      return true;
    },
    {
      // TODO: Add Intent
      intent: undefined
    }
  );
};

export const addField = (
  props: {
    processId: string;
    fieldData?: Partial<Field>;
    index?: number;
  } & ( // added this type to keep the call signature verbose
    | {
        stepId: string;
        swimlaneId?: undefined;
        shouldCreateHostStep?: false;
        shouldCreateHostSwimlaneAndStep?: false;
      }
    | {
        swimlaneId: string;
        stepId?: undefined;
        shouldCreateHostStep: true;
        shouldCreateHostSwimlaneAndStep?: false;
      }
    | {
        shouldCreateHostSwimlaneAndStep: true;
        stepId?: undefined;
        swimlaneId?: undefined;
        shouldCreateHostStep?: false;
      }
  )
) => {
  const {
    processId,
    fieldData,
    index,
    shouldCreateHostSwimlaneAndStep,
    shouldCreateHostStep
  } = props;

  // Why let?: If host step or host swimlane needs to created
  // StepId and SwimlaneId will be undefined and needs to be reassigned
  let { stepId, swimlaneId } = props;

  const newField = createField({ processId, props: fieldData });

  return dbOps().transaction(
    () => {
      if (shouldCreateHostSwimlaneAndStep) {
        [stepId, swimlaneId] = addStep({
          processId,
          stepData: { fields: [newField.id] },
          shouldCreateHostSwimlane: true
        });
      }

      if (shouldCreateHostStep && swimlaneId) {
        [stepId] = addStep({
          processId,
          swimlaneId,
          stepData: { fields: [newField.id] }
        });
      }

      if (!stepId) return;

      const step = dbOps().getStep(processId, stepId);
      if (!step) return;

      dbOps().addField(processId, newField);
      if (!shouldCreateHostSwimlaneAndStep && !shouldCreateHostStep) {
        const newFieldSequence = insertItemAtIndex({
          array: step.fields,
          item: newField.id,
          index
        });
        dbOps().updateStep(processId, stepId, { fields: newFieldSequence });
      }

      return [newField.id, stepId, swimlaneId] as const;
    },
    {
      intent: Intents.ADD_FIELD
    }
  );
};

export const deleteField = ({
  processId,
  parentStepId,
  fieldId
}: {
  processId: string;
  parentStepId: string;
  fieldId: string;
}) => {
  dbOps().transaction(
    () => {
      const step = dbOps().getStep(processId, parentStepId);
      if (!step) return;

      const newFieldSequence = step.fields.filter(field => field !== fieldId);

      dbOps().updateStep(processId, parentStepId, { fields: newFieldSequence });
      dbOps().removeField(processId, fieldId); // This cascade deletes all related rules too
    },
    {
      intent: Intents.DELETE_FIELD
    }
  );
};

export const updateField = ({
  processId,
  fieldId,
  fieldProps,
  shouldSkipUpdatingModifiedAt
}: {
  processId: string;
  fieldId: string;
  fieldProps: Partial<Field>;
  shouldSkipUpdatingModifiedAt?: boolean;
}) =>
  dbOps().transaction(
    () =>
      dbOps().updateField(
        processId,
        fieldId,
        fieldProps,
        shouldSkipUpdatingModifiedAt
      ),
    { intent: Intents.UPDATE_FIELD }
  );

export const moveField = (props: {
  processId: string;
  fieldId: string;
  targetStepId: string;
  targetIndex: number;
}) => {
  const { processId, fieldId, targetStepId, targetIndex } = props;

  const sourceStep = dbOps().getStepByFieldId(processId, fieldId);
  if (!sourceStep) return;

  const sourceStepId = sourceStep.id;

  dbOps().transaction(
    () => {
      const sourceStep = dbOps().getStep(processId, sourceStepId);
      const targetStep = dbOps().getStep(processId, targetStepId);

      if (!sourceStep || !targetStep) return;

      if (sourceStepId === targetStepId) {
        const newSourceFieldSequence = reorderItems({
          array: sourceStep.fields,
          item: fieldId,
          targetIndex
        });

        dbOps().updateStep(processId, sourceStepId, {
          fields: newSourceFieldSequence
        });
      } else {
        const [newSourceFieldSequence, newTargetFieldSequence] =
          moveItemToAnotherArray({
            sourceArr: sourceStep.fields,
            targetArr: targetStep.fields,
            item: fieldId,
            targetIndex
          });

        dbOps().updateStep(processId, sourceStepId, {
          fields: newSourceFieldSequence
        });
        dbOps().updateStep(processId, targetStepId, {
          fields: newTargetFieldSequence
        });
      }
    },
    { intent: Intents.REORDER_FIELD }
  );
};
